/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { css } from "@emotion/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import type { SectionItem } from "@packages/sdk";
import type { SectionItemCollection } from "@packages/sdk/src/lib/types/schema/collection/sectionItemCollectionSchema";
import { HallowDataTestId } from "@tools/playwright/global";

import { JoinChallengeButton } from "../../../../../../emotion";
import { getChallengeStatus } from "../../../../../utils/content";
import { formatCompactNumber } from "../../../../../utils/numberFormat";
import { Button } from "../../../_core/Button/Button.emotion";
import {
  CheckmarkIcon,
  RadioIcon,
  VideoIcon,
} from "../../../icons/Icon/GeneralIcons";
import { ChallengeCheckmark } from "../../challenges/ChallengeCheckmark/ChallengeCheckmark.emotion";
import { ChallengeProgressBar } from "../../challenges/ChallengeProgressBar/ChallengeProgressBar.emotion";
import type { ContentLargeProps } from "../../content/ContentLarge/ContentLarge.emotion";
import type { ContentMediumProps } from "../../content/ContentMedium/ContentMedium.emotion";
import { CollectionMoreMenu } from "../../content/ContentMoreMenu/CollectionMoreMenu.emotion";
import { PrayerMoreMenu } from "../../content/ContentMoreMenu/PrayerMoreMenu.emotion";
import type { ContentSmallProps } from "../../content/ContentSmall/ContentSmall.emotion";
import type { BaseGetSectionItemProps } from "./Section.emotion";

const joinButtonStyle: Interpolation = css({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
});

const progressBarContainerStyle: Interpolation = css({
  width: "60px",
});

/**
 * TODO: ContentSmallDropdownModal (Favorite, ShareModal)
 * TODO: MediaPlayer
 */
export type GetSectionItemContentPropsProps =
  BaseGetSectionItemProps<SectionItem> & {
    size: "s" | "m" | "l";
  };

const getImgContainerChildren = ({
  referenceData,
  size,
  t,
}: {
  referenceData: SectionItemCollection;
  size: GetSectionItemContentPropsProps["size"];
  t: Function;
}) => {
  if (size === "m" && referenceData?.type === "challenge") {
    const challengeStatus = getChallengeStatus(
      referenceData.available_at,
      referenceData.ends_at,
    );
    if (!referenceData.has_joined) {
      return (
        <JoinChallengeButton
          size="s"
          css={joinButtonStyle}
          collection={referenceData}
          showSmsPromptPopup={false}
        >
          {challengeStatus === "ended"
            ? t("start_cta_for_plans")
            : t("community_cta_join")}
        </JoinChallengeButton>
      );
    }
    if (referenceData.is_completed) {
      return <ChallengeCheckmark />;
    }
    if (referenceData.has_joined) {
      return (
        <ChallengeProgressBar
          progress={referenceData.progress || 0}
          css={progressBarContainerStyle}
        />
      );
    }
  }
  return undefined;
};

export const getSectionItemContentProps = ({
  analytics,
  item,
  itemTypeLinkSearchParams = {},
  section,
  link,
  size,
  pageId,
  screenName,
  t,
}: GetSectionItemContentPropsProps):
  | ContentSmallProps
  | ContentMediumProps
  | ContentLargeProps => {
  const referenceCollection = item?.reference?.collection;
  const referenceData = referenceCollection ?? item?.reference;
  const imageSrc = referenceData?.images;

  const title = referenceData?.name ?? referenceData?.title;
  const description =
    referenceData?.short_desc ??
    referenceData?.label_desc ??
    referenceData?.description;
  // This is a workaround to deal with the fact that it takes ~10-30 seconds for the users_count to be updated on the BE, whilst place_in_line is updated immediately
  const usersCount =
    referenceData?.place_in_line > referenceData?.users_count
      ? referenceData?.place_in_line
      : referenceData?.users_count;
  const metadata = usersCount
    ? t("challenge_tile_join_count", {
        "0": formatCompactNumber(usersCount),
      })
    : referenceData?.lengths
      ? referenceData?.lengths
      : referenceData?.sessions
        ? referenceData?.sessions
        : null;
  const href =
    referenceCollection || item?.reference_type === "collection"
      ? `/collections/${referenceData?.id}`
      : item?.reference_type === "radio_station"
        ? `/radio-stations/${referenceData?.id}`
        : `/prayers/${referenceData?.id}`;

  const isUnjoinedChallenge =
    referenceData?.type === "challenge" && !referenceData?.has_joined;

  const challengeStatus = getChallengeStatus(
    referenceData?.available_at,
    referenceData?.ends_at,
  );

  const showCheckmarkJoinedIcon =
    size === "m" &&
    referenceData?.type === "challenge" &&
    referenceData?.has_joined;

  const collectionId =
    referenceCollection || item?.reference_type === "collection"
      ? referenceData?.id
      : undefined;

  const dataTestId =
    size === "s"
      ? collectionId
        ? `${HallowDataTestId.ContentSmall}-${collectionId}`
        : HallowDataTestId.ContentSmall
      : size === "m"
        ? collectionId
          ? `${HallowDataTestId.ContentMedium}-${collectionId}`
          : HallowDataTestId.ContentMedium
        : undefined;

  const props: ContentSmallProps | ContentMediumProps | ContentLargeProps = {
    asChild: link,
    ...(size === "m" && isUnjoinedChallenge && challengeStatus !== "ended"
      ? { imgBlur: true }
      : {}),
    ...(dataTestId ? { "data-testid": dataTestId } : {}),
    children: link ? (
      <Link
        href={`${href}?${new URLSearchParams(itemTypeLinkSearchParams[item?.reference_type]).toString()}`}
        onPointerUp={() => {
          if (size === "m" && isUnjoinedChallenge) {
            return;
          }
          analytics?.track({
            event: HallowAnalyticsEvent.TappedSectionItem,
            properties: {
              // Refactor: This event should be logged for every Section Item (move event up DOM tree)
              page_id: pageId,
              screen_name: screenName ?? null,
              section_id: section?.id,
              section_item_id: item?.item_id ?? null,
              section_title: section?.title ?? null,
              section_type: section?.type ?? null,
              index: section?.items.indexOf(item) ?? null,
              item_type: item?.type ?? null,
            },
          });
        }}
      />
    ) : null,
    description: description,
    imgSrc: imageSrc,
    metadata: metadata,
    metadataInlineIcon: showCheckmarkJoinedIcon ? (
      <CheckmarkIcon.Inline />
    ) : referenceData?.has_video ? (
      <VideoIcon.Filled />
    ) : null,
    subscription: !(referenceData?.has_access ?? item?.reference?.has_access),
    title: title,
  };

  if (item.reference_type === "radio_station") {
    props["metadata"] = t("web_radio_station");
    props["metadataInlineIcon"] = <RadioIcon />;
  }

  if (size === "m") {
    props["imgContainerProps"] = {
      children: getImgContainerChildren({
        referenceData,
        size,
        t,
      }),
    };
  }

  if (size === "l") {
    const href =
      referenceCollection || item?.reference_type === "collection"
        ? `/collections/${referenceData?.id}`
        : `/prayers/${referenceData?.id}`;
    props["actions"] = [
      link ? (
        <Button
          asChild
          key="links"
          variant="lighten"
          onClickCapture={() => {
            analytics.track({
              event: HallowAnalyticsEvent.TappedDetails,
              properties: {
                section_item_id: item?.item_id ?? null,
                collection_id: referenceCollection?.id ?? null,
              },
            });
          }}
        >
          <Link href={href}>{t("web_details")}</Link>
        </Button>
      ) : null,
    ];
    props["color"] = referenceData?.images
      ?.color_hex as ContentLargeProps["color"];
  }

  if (size === "s") {
    props["rightActions"] =
      item?.reference?.collection ||
      item?.reference_type === "collection" ||
      item?.reference_type === "challenge"
        ? [
            <CollectionMoreMenu
              closeOnScroll
              collection={{
                id: referenceData?.id,
                title,
                desc: description,
                sessions: metadata,
                has_access: referenceData?.has_access,
                has_video: referenceData?.has_video,
              }}
              imageSrc={imageSrc}
              key={`${item.id}_more_menu`}
            />,
          ]
        : item?.reference_type === "prayer"
          ? [
              <PrayerMoreMenu
                closeOnScroll
                imageSrc={imageSrc}
                key={`${item.id}_more_menu`}
                prayer={{
                  id: referenceData?.id,
                  is_favorite: referenceData?.is_favorite,
                  title,
                  label_desc: description,
                  lengths: metadata,
                  has_access: referenceData?.has_access,
                  has_video: referenceData?.has_video,
                }}
              />,
            ]
          : console.log(item);
  }

  return props;
};
